import {
  Button,
  Flexbox,
  Heading,
  Icons,
  Positions,
  Sizes,
  Text,
  TextElementTypes,
} from '@sede-enterprise/shell-ds-react-framework';
import {
  FullScreen,
  FullScreenExit,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { Legend } from 'components/Charting/Legend';
import { OptimisationChartDataContext } from 'components/Charting/Optimisation/OptimisationChartBuilder';
import { isEmptyRecordOfArrays } from 'components/Charting/utils';
import { FullScreenModal } from 'components/FullScreenModal/FullScreenModal';
import { FullScreenModalChartWrapper } from 'components/FullScreenModal/FullScreenModal.styled';
import { SectionHeader } from 'components/Layout/SectionHeader/SectionHeader';
import { GET_COMPANY } from 'components/NavBar/queries';
import { InfoCircleTooltip } from 'components/Tooltip/InfoCircleTooltip';
import { useChartDataFetcher } from 'hooks/useChartDataFetcher/useChartDataFetcher';
import { useDimensions } from 'hooks/useDimensions/useDimensions';
import { usePlutoApiQuery } from 'hooks/useQuery/plutoApi';
import { ContractedVolumeContainer } from 'pages/Optimisation/components/ContractedVolume/ContractedVolume.styled';
import {
  AllTradeMarkets,
  ContractedVolumeTooltipProps,
  MarketKey,
} from 'pages/Optimisation/components/ContractedVolume/types';
import { ContractedVolumeTooltip } from 'pages/Optimisation/components/ContractedVolumeTooltip';
import { DownloadModal } from 'pages/Optimisation/components/DownloadModal/DownloadModal';
import { OptimisationChart } from 'pages/Optimisation/components/OptimisationChart';
import {
  CHART_CONSTANTS,
  MARKET_FILTER_VALUES,
} from 'pages/Optimisation/constants';
import { getColourMap } from 'pages/Optimisation/helper-functions';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useStore } from 'store';
import { useDeepCompareMemo } from 'use-deep-compare';
import { getTradingDayRange } from 'utils/optimisation';

const OptimisationChartMemo = memo(OptimisationChart);

const { Download } = Icons;

interface ContractedVolumeProps {
  marketFilter: MARKET_FILTER_VALUES;
}

type MarketFilterItems = {
  label: string;
  identifier: AllTradeMarkets | 'disabled' | 'other';
  market:
    | MarketKey.Wholesale
    | MarketKey.Ancillary
    | MarketKey.BalancingMechanism
    | 'other';
};

/**
 * Hack to implement a "buttonDisabled" functionality in Card
 */
const useCardButtonDisabledHack = (disabled: boolean) => {
  const cardRef = useRef<HTMLDivElement | null>(null);

  const buttonElement: Element | undefined = cardRef.current?.querySelector(
    "[data-testid='card-action-button']",
  ) as Element | undefined;

  (() => {
    if (!buttonElement) {
      return;
    }

    if (!disabled) {
      buttonElement.removeAttribute('disabled');
    } else {
      buttonElement.setAttribute('disabled', '');
    }

    // setting the button to disabled is almost enough, apart from the SVG icon keeping it's color
    // so we grab the SVG path and update the fill manually :)

    const svgIcon = buttonElement.querySelector('path')!;

    svgIcon.style.fill = !disabled ? '#00205c' : '#99a6be';
  })();

  return cardRef;
};

const useFullScreenModalSiteAndDateString = (
  companyId: string | undefined,
  siteId: string | undefined,
  currentDate: Date,
) => {
  const { data: companyData } = usePlutoApiQuery(GET_COMPANY, {
    skip: !companyId,
    variables: { id: companyId },
  });

  const siteName = companyData?.company?.sites?.find(
    (it: { id: string }) => it.id === siteId,
  )?.name;

  const currentDateParts = currentDate.toDateString().split(' ');

  return `${siteName}, ${currentDateParts[2]} ${currentDateParts[1]} ${currentDateParts[3]}`;
};

export const ContractedVolume = ({ marketFilter }: ContractedVolumeProps) => {
  const { company, site } = useParams();

  const [currentDate, appTheme] = useStore((state) => [
    state.currentDate,
    state.appTheme,
  ]);

  const { isMobile, isMobileOrTablet, viewportWidth } = useDimensions();

  const [tooltipData, setTooltipData] = useState<
    ContractedVolumeTooltipProps | undefined
  >();

  const { errors, data, isInitialLoading } = useChartDataFetcher(
    site,
    currentDate,
  );

  const [colorMapFilter, setColorMapFilter] = useState<
    MARKET_FILTER_VALUES | AllTradeMarkets
  >(MARKET_FILTER_VALUES.All);

  useEffect(() => {
    // When market filter changed, reset color map filter to match
    if (marketFilter !== colorMapFilter) {
      setColorMapFilter(marketFilter);
    }
  }, [marketFilter]);

  const colorMap = useMemo(
    () =>
      getColourMap(data ? colorMapFilter : MARKET_FILTER_VALUES.All, appTheme),
    [data, colorMapFilter],
  );
  const {
    isChangeOverDay,
    dateRange: displayDateRange,
    changeOverType,
  } = getTradingDayRange(currentDate);

  const dataContext = useDeepCompareMemo(() => {
    const context: OptimisationChartDataContext | undefined =
      site && data
        ? {
            appTheme,
            displayDateRange,
            dataSubject: site,
            data,
            errors,
            loading: isInitialLoading,
            colorMap,
            setTooltipData,
          }
        : undefined;

    return context;
  }, [
    appTheme,
    currentDate,
    site,
    data,
    colorMap,
    setTooltipData,
    displayDateRange,
    isInitialLoading,
    errors,
  ]);

  const filteredLegendValues = useMemo(() => {
    if (!dataContext) {
      return [];
    }

    const marketFilterItems: MarketFilterItems[] =
      CHART_CONSTANTS.legendLabels[marketFilter] ?? [];

    return marketFilterItems.filter((filter) => {
      return (
        filter.market === 'other' ||
        dataContext.data[filter.market]?.some(
          (item) => item.market === filter.identifier,
        )
      );
    });
  }, [dataContext, marketFilter]);

  const allTradesEmptyArrays =
    !!dataContext && isEmptyRecordOfArrays((dataContext as any)?.data);

  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const closeDownloadModal = () => setShowDownloadModal(false);
  const download = () => setShowDownloadModal(true);

  const cardRef = useCardButtonDisabledHack(!download);

  const hasData = !isEmptyRecordOfArrays(data);

  const volumesOverviewTitle = (
    <Flexbox alignItems="center" style={{ flexShrink: 0 }}>
      <Heading
        type={TextElementTypes.Span}
        bold
        level={5}
        prominence={'strong'}
      >
        Volumes overview
      </Heading>
      <InfoCircleTooltip
        tooltip={
          <Text type="span" size={Sizes.Small}>
            All trades for your site, shown in megawatts over the trade
            duration. Please note that each individual trade is shown, the total
            volume of which may exceed the total capacity of the site as the
            trades can be netted off against each other
          </Text>
        }
        infoCircleStyle={{ marginTop: '2px' }}
      />
    </Flexbox>
  );

  const downloadButton = (
    <Button
      variant="outlined"
      icon={<Download />}
      onClick={download}
      {...(isMobile
        ? { iconOnly: true, 'aria-label': 'Download trades' }
        : { iconPosition: Positions.Right })}
      style={{ flexShrink: 0 }}
    >
      <b>Download</b>
    </Button>
  );

  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  const toggleFullscreen = () => setIsFullScreenOpen((current) => !current);

  const fullScreenButton = (
    <Button
      variant="outlined"
      icon={isFullScreenOpen ? <FullScreenExit /> : <FullScreen />}
      onClick={toggleFullscreen}
      disabled={!dataContext || allTradesEmptyArrays}
      iconOnly
      aria-label={isFullScreenOpen ? 'Exit full screen' : 'Open full screen'}
      style={{ flexShrink: 0 }}
    />
  );

  const fullScreenModalSiteAndDateString = useFullScreenModalSiteAndDateString(
    company,
    site,
    currentDate,
  );

  const isMobileAndTabletLandscape =
    isMobileOrTablet && window.screen.orientation?.type === 'landscape-primary';

  // applying different sizing to full screen chart modal when the mobile size is narrow
  const isNarrowMobileView =
    (window.innerHeight < 500 && viewportWidth > 500) ||
    (window.innerHeight > 500 && viewportWidth < 500);

  // prevent background scrolling when modal open
  useEffect(() => {
    if (isFullScreenOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isFullScreenOpen]);

  const fullScreen = (
    <FullScreenModal
      open
      $narrowMobile={isNarrowMobileView}
      $mobileLandscape={isMobileAndTabletLandscape}
      onClose={toggleFullscreen}
      header={{
        content: (
          <Flexbox
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            style={{
              width: '100%',
              paddingLeft: 8 - 16,
              paddingRight: 24,
              paddingTop: 16 - 8,
              paddingBottom: 16 - 8,
            }}
            gap="16px"
          >
            {volumesOverviewTitle}
            <Flexbox alignItems={'center'} gap={'16px'}>
              {!isMobileOrTablet && (
                <span>{fullScreenModalSiteAndDateString}</span>
              )}
              {downloadButton}
              {fullScreenButton}
            </Flexbox>
          </Flexbox>
        ),
      }}
      closeButton={false}
      contentScrollable={false}
      padding={false}
      centered
      size={Sizes.Small}
      $showMask={false}
      $appTheme={appTheme}
      focusTriggerAfterClose
    >
      <FullScreenModalChartWrapper $narrowMobile={isNarrowMobileView}>
        <OptimisationChartMemo fullScreen dataContext={dataContext} />
        {hasData && !isNarrowMobileView && (
          <Legend colorMap={colorMap} items={filteredLegendValues} />
        )}
      </FullScreenModalChartWrapper>
      {hasData && isNarrowMobileView && (
        <div style={{ paddingBottom: '24px' }}>
          <Legend colorMap={colorMap} items={filteredLegendValues} />
        </div>
      )}
    </FullScreenModal>
  );

  const onLegendItemClick = (identifier: string) => {
    if (identifier === colorMapFilter) {
      setColorMapFilter(marketFilter as MARKET_FILTER_VALUES);
    } else {
      setColorMapFilter(identifier as AllTradeMarkets);
    }
  };

  return (
    <ContractedVolumeContainer ref={cardRef}>
      <DownloadModal
        showModal={showDownloadModal}
        onClose={closeDownloadModal}
        displayDateRange={displayDateRange}
      />
      <SectionHeader style={{ padding: 24 }}>
        <Flexbox
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          style={{ width: '100%' }}
          gap="16px"
        >
          {volumesOverviewTitle}
          <Flexbox alignItems={'center'} gap={'16px'}>
            {downloadButton}
            {fullScreenButton}
            {isFullScreenOpen && fullScreen}
          </Flexbox>
        </Flexbox>
      </SectionHeader>
      <OptimisationChartMemo fullScreen={false} dataContext={dataContext} />
      {tooltipData ? (
        <ContractedVolumeTooltip
          isChangeOverDay={isChangeOverDay}
          changeOverType={changeOverType}
          {...tooltipData}
        />
      ) : null}
      {hasData && (
        <Legend
          colorMap={colorMap}
          items={filteredLegendValues}
          onLegendItemClick={onLegendItemClick}
        />
      )}
    </ContractedVolumeContainer>
  );
};
